// Layer 3: one pure money projection for GM and Superintendent Overview.
// Cost owns the closed-day P&L; Planning owns Year-1 scenarios. No new rates.
import { costPeriod, daysForPeriod, stateForDay, type CostPeriod } from '../cost/Scene.transform';
import { costPL } from '../cost/CostPLTable.transform';
import { BUDGET_TONNES_PER_DAY } from '../cost/seed';
import { annualPlanView, rollingForecastView } from '../planning/Scene.transform';
import { PLANNING_MONTH_PROFILE } from '@/versions/v1/shared/bunker-hill-planning-seed';
import { INITIAL_STATE } from '@/versions/v1/shared/ScenarioEngine';
import { nsrRead, readingNum } from '@/versions/v1/shared/NsrEngine';
import { formatCurrencyStandard as formatCurrency } from '@/versions/v1/shared/money';
import { bm01BearingStopComparison } from '../downtime/ValueLost.transform';
import { BUNKER_HILL_SMELTER_SCHEDULE } from '@/versions/v1/shared/bunker-hill-smelter-terms';

/** Status compares to the named Budget — the M5 target (Month-4 recovery at
 * 1,440 st/d) — never to design; lower costs are favourable.
 * Five percent adverse is amber, larger is red. Negative budgets work too. */
export function gmBudgetTone(actual: number, budget: number, direction: 'higher' | 'lower' = 'higher'): 'ok' | 'warn' | 'bad' {
  const favourable = (actual-budget) * (direction === 'lower' ? -1 : 1);
  if (favourable >= -0.000001) return 'ok';
  return -favourable > Math.abs(budget)*0.05 ? 'bad' : 'warn';
}
export const gmAmount = (value: number) => `${value < 0 ? '−' : ''}${formatCurrency(Math.abs(value))}`;
export const gmThousands = (value: number) => (value/1000).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const gmSignedAmount = (value: number) => `${value >= 0 ? '+' : '−'}${formatCurrency(Math.abs(value))}`;
export const GM_CLOSED_PERIOD = '1–7 Jun';

/** `period` defaults to the GM landing's closed month; Cost & revenue reads the
 * same projection for its own period switch (polish.P7), so the two bridges are
 * one function. */
export function gmMoney(period: CostPeriod = 'month') {
  const view = costPeriod(period);
  const pl = costPL(period);
  const days = daysForPeriod(period);
  // #736: this projection has no session cursor of its own — its basis is the
  // closed month (`costPeriod`). The Year-1 Planning views it surfaces are read
  // at the demo cursor, exactly as on dev; `/planning` is the cursor-driven
  // route.
  const annual = annualPlanView(INITIAL_STATE);
  const rolling = rollingForecastView(INITIAL_STATE);
  const m4 = PLANNING_MONTH_PROFILE.find(m=>m.id==='m4')!;
  const budgetTonnes = days.length*BUDGET_TONNES_PER_DAY;
  const budgetRecoveries = { pb:m4.budgetPbRecoveryPct, zn:m4.budgetZnRecoveryPct, ag:m4.budgetAgRecoveryPct };
  // Budget is the M5 target: Month-4 budget tonnes and recoveries priced
  // through NsrEngine at the same US$ deck. Budget-at-actual-tonnes isolates the
  // volume step; actual recoveries then isolate recovery. Cost's design-
  // recovery figure stays separately named "design potential".
  let budgetRevenueAtBudgetTonnes = 0;
  let budgetRevenueAtActualTonnes = 0;
  let recoveryRevenueStage = 0;
  const budgetRevenueByProduct = { pb:0, zn:0, ag:0 };
  for (const day of days) {
    const state = stateForDay(day.date);
    const read = nsrRead(state, m4.budgetStpd, { actual: budgetRecoveries }).daily;
    budgetRevenueAtBudgetTonnes += read.total;
    budgetRevenueByProduct.pb += read.pb.netValue;
    budgetRevenueByProduct.zn += read.zn.netValue;
    budgetRevenueByProduct.ag += read.ag.netValue;
    budgetRevenueAtActualTonnes += nsrRead(state, day.tonnesMilled, { actual: budgetRecoveries }).daily.total;
    recoveryRevenueStage += nsrRead(state, day.tonnesMilled, { actual: day.recoveries }).daily.total;
  }
  const budgetEbitda = budgetRevenueAtBudgetTonnes - pl.total.total.budget;
  const metric = (actual: number, design: number, target: number, direction: 'higher' | 'lower' = 'higher') => ({actual,budget:design,target,tone:gmBudgetTone(actual,target,direction)});
  const mtd = {
    revenue: metric(pl.revenue.actual,pl.revenue.budget,budgetRevenueAtBudgetTonnes),
    cost: metric(pl.total.total.actual,pl.total.total.budget,pl.total.total.budget,'lower'),
    ebitda: metric(pl.margin.actual,pl.margin.budget,budgetEbitda),
  };
  // Ordered counterfactuals: Budget -> actual tonnes at Budget recoveries ->
  // actual recoveries -> actual assays -> actual costs. The volume step carries
  // the variable cost of the extra tonnes (its budget unit margin); the cost
  // step carries the remaining, non-volume variance. Both decks and the feed
  // are equal, so the price step is zero.
  const variableCost = pl.rows.reduce((sum,row)=>sum+(row.variable.actual-row.variable.budget), 0);
  const tonneDelta = view.tonnesMilled - budgetTonnes;
  const prices = BUNKER_HILL_SMELTER_SCHEDULE.prices;
  const budgetPrices = { 'Pb concentrate': prices.pbPerTonne, 'Zn concentrate': prices.znPerTonne, 'Ag payable': prices.agPerOz };
  const priceEffect = view.revenueRows.reduce((sum, row) => sum + row.payableMetalRaw * (row.priceRaw - budgetPrices[row.product]), 0);
  const bridge = [
    { id:'volume', label:`Volume · ${tonneDelta>=0?'+':'−'}${Math.abs(tonneDelta).toLocaleString()} dst`, value:(budgetRevenueAtActualTonnes-budgetRevenueAtBudgetTonnes)-variableCost },
    { id:'recovery', label:'Recovery vs budget', value:recoveryRevenueStage-budgetRevenueAtActualTonnes },
    { id:'price', label:'Price · same budget deck', value:priceEffect },
    { id:'assay', label:'Conc. assays vs budget', value:pl.revenue.actual-recoveryRevenueStage },
    { id:'cost', label:'Non-volume cost vs budget', value:pl.total.total.budget-pl.total.total.actual+variableCost },
  ];
  const actualCostPerSt = pl.total.total.actual/view.tonnesMilled;
  const budgetCostPerSt = pl.total.total.budget/budgetTonnes;
  const scenarios = rolling.scenarios.map(s=>({...s,ebitda:s.fullYearNsr-s.fullYearTonnes*actualCostPerSt}));
  const budget = {tonnes:annual.annualBudgetSt,nsr:rolling.budgetNsr,znRecovery:annual.annualZnRecoveryBudgetPct,ebitda:rolling.budgetNsr-annual.annualBudgetSt*budgetCostPerSt};
  const reagentOverrun = pl.categoryActual.Reagents-pl.categoryBudget.Reagents;
  const reagentCostPerSt = pl.categoryActual.Reagents/view.tonnesMilled;
  // Cost's design-recovery budget split by product, kept beside the M5 Budget
  // so the two comparators can never be confused on the glass.
  const designRevenueByProduct = days.reduce((total, day) => {
    const state = stateForDay(day.date);
    // Cost's budget uses Pb design recovery for Ag, as NsrEngine does.
    const pb = readingNum('rd-kpi-pb-rec', state, 'design');
    const zn = readingNum('rd-kpi-zn-rec', state, 'design');
    const read = nsrRead(state, day.tonnesMilled, { actual: { pb, zn, ag: pb } }).daily;
    return { pb: total.pb + read.pb.netValue, zn: total.zn + read.zn.netValue, ag: total.ag + read.ag.netValue };
  }, { pb: 0, zn: 0, ag: 0 });
  return { mtd, bridge, actualCostPerSt, budgetCostPerSt, reagentOverrun, reagentCostPerSt,
    tonnes:view.tonnesMilled, budgetTonnes, dateRange:view.dateRange,
    categories:pl.rows, revenueByProduct:view.revenueByProduct, designRevenueByProduct, budgetRevenueByProduct,
    annual, forecast:{scenarios,budget}, bearingStop:bm01BearingStopComparison(),
  };
}
export function gmPnlCaption(key: 'revenue' | 'cost' | 'ebitda'): string {
  const m = gmMoney().mtd[key];
  const caption = `vs Budget ${gmAmount(m.target)} · ${gmSignedAmount(m.actual-m.target)}`;
  return key==='cost' ? caption : `${caption} · design potential ${gmAmount(m.budget)}`;
}
